import { Phone } from 'lucide-react';
import type { CampaignData, PriceType, PromotionType } from '@/lib/types';

// Google SERP palette, matched to the inline ad-preview mock in the ad page.
const LINK = '#1a0dab';
const BODY = '#4d5156';
const DARK = '#202124';

// Google renders a bounded selection of assets per impression, not the full pool.
// Cap the preview to realistic SERP counts so it stops implying every asset shows
// at once (#6097).
const MAX_PREVIEW_SITELINKS = 4;
const MAX_PREVIEW_CALLOUTS = 4;
const MAX_PREVIEW_SNIPPETS = 2;

function formatPrice(value: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: value % 1 === 0 ? 0 : 2,
    }).format(value);
  } catch {
    // Unknown/empty currency code — fall back to a plain "USD 10" style.
    return `${currency ? `${currency} ` : ''}${value}`;
  }
}

function promotionLabel(p: PromotionType): string {
  const amount =
    p.discount_type === 'percent off'
      ? `${p.discount_modifier}% off`
      : `${formatPrice(p.discount_modifier, p.currency ?? 'USD')} off`;
  const parts: string[] = [];
  if (p.occasion) parts.push(p.occasion);
  parts.push(amount);
  if (p.promotion_target) parts.push(`on ${p.promotion_target}`);
  let label = parts.join(' ');
  if (p.promotion_code) label += ` (code: ${p.promotion_code})`;
  return label;
}

interface AdPreviewExtensionsProps {
  campaign: CampaignData;
  previewMode: 'desktop' | 'mobile';
}

/**
 * Renders the campaign-level ad assets (callouts, structured snippets, promotions,
 * prices, call extension and sitelinks) beneath the ad headline/description in the
 * ad preview, following Google SERP conventions (#5355). The logo and image
 * extension live in the preview's header/body layout and are handled by the caller.
 */
export function AdPreviewExtensions({ campaign, previewMode }: AdPreviewExtensionsProps) {
  const isDesktop = previewMode === 'desktop';

  // Google serves the most specific level only, so inherited account callouts appear in the
  // preview only when the campaign has none of its own (#6353).
  const nonEmpty = (t: string | undefined): t is string => !!t && t.trim().length > 0;
  const campaignCallouts = (campaign.callouts ?? []).map((c) => c.text).filter(nonEmpty);
  const callouts = (
    campaignCallouts.length > 0
      ? campaignCallouts
      : (campaign.account_callouts ?? []).map((ac) => ac.callout_text).filter(nonEmpty)
  ).slice(0, MAX_PREVIEW_CALLOUTS);

  const snippets = (campaign.structured_snippets ?? [])
    .filter((s) => s.values?.length)
    .slice(0, MAX_PREVIEW_SNIPPETS);
  const promotions = campaign.promotions ?? [];
  const prices = campaign.prices ?? [];
  const sitelinks = (campaign.site_links ?? []).slice(0, MAX_PREVIEW_SITELINKS);
  const phone = campaign.phone_numbers?.find((p) => p.call_extension_enabled);

  const hasAny =
    callouts.length > 0 ||
    snippets.length > 0 ||
    promotions.length > 0 ||
    prices.length > 0 ||
    sitelinks.length > 0 ||
    !!phone;

  if (!hasAny) return null;

  return (
    <div className="mt-2 space-y-2">
      {/* Callouts — dot-separated inline line */}
      {callouts.length > 0 && (
        <p className="text-sm leading-relaxed" style={{ color: BODY }}>
          {callouts.join('  ·  ')}
        </p>
      )}

      {/* Structured snippets — "Header: value · value · value" per line */}
      {snippets.map((s, i) => (
        <p key={`snippet-${i}`} className="text-sm leading-relaxed" style={{ color: BODY }}>
          <span className="font-medium">{s.header_type}:</span>{' '}
          {s.values.map((v) => v.value).join('  ·  ')}
        </p>
      ))}

      {/* Promotions */}
      {promotions.map((p, i) => (
        <p key={`promo-${i}`} className="text-sm leading-relaxed" style={{ color: BODY }}>
          {promotionLabel(p)}
        </p>
      ))}

      {/* Call extension */}
      {phone && (
        <div className="flex items-center gap-1.5 text-sm" style={{ color: LINK }}>
          <Phone className="h-3.5 w-3.5" />
          <span>{phone.value}</span>
        </div>
      )}

      {/* Prices — row of small cards */}
      {prices.length > 0 && (
        <div className={`flex gap-2 ${isDesktop ? 'flex-wrap' : 'overflow-x-auto'}`}>
          {prices.map((pr: PriceType, i) => (
            <div
              key={`price-${i}`}
              className="flex-shrink-0 rounded-md border border-gray-200 px-3 py-1.5"
              style={{ minWidth: '7rem' }}
            >
              <div className="text-xs font-semibold" style={{ color: DARK }}>
                {pr.header || pr.type}
              </div>
              <div className="text-sm" style={{ color: LINK }}>
                {/* 'no qualifier' / 'no units' are the stored sentinels for None — omit them
                    instead of printing them around the price (matches the prices table). */}
                {pr.qualifier && pr.qualifier !== 'no qualifier' ? `${pr.qualifier} ` : ''}
                {formatPrice(pr.value, pr.currency)}
                {pr.frequency && pr.frequency !== 'no units' ? ` ${pr.frequency}` : ''}
              </div>
              {pr.description && (
                <div className="text-xs leading-tight" style={{ color: BODY }}>
                  {pr.description}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Sitelinks — 2-col grid on desktop, single column on mobile */}
      {sitelinks.length > 0 && (
        <div className={`grid gap-x-8 gap-y-2 pt-1 ${isDesktop ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {sitelinks.map((sl, i) => (
            <div key={`sitelink-${i}`} className="min-w-0">
              <span className="text-sm hover:underline cursor-pointer" style={{ color: LINK }}>
                {sl.name}
              </span>
              {isDesktop && (sl.description1 || sl.description2) && (
                <div className="text-xs leading-tight" style={{ color: BODY }}>
                  {sl.description1 && <div>{sl.description1}</div>}
                  {sl.description2 && <div>{sl.description2}</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
